import { ChevronRight, Eye, Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

type EntityGroupCardProps = {
	entityId: string;
	entityName: string;
	index: number;
	initials: string;
	iconClassName: string;
	childItems: string[];
	childLabelSingular: string;
	childLabelPlural: string;
	emptyChildrenTitle: string;
	emptyChildrenDescription: string;
	createChildText: string;
	onEditEntity: () => void;
	onDeleteEntity: () => void;
	onEditChild: (childIndex: number) => void;
	onRequestDeleteChild: (childIndex: number) => void;
	onViewDetails: () => void;
	onCreateChild: () => void;
	alwaysShowDetailsButton?: boolean;
	/** Ex.: listar colaboradores do departamento (ícone à esquerda do rodapé). */
	onViewMembers?: () => void;
};

export const EntityGroupCard = ({
	entityId,
	entityName,
	index: _index,
	initials,
	iconClassName,
	childItems,
	childLabelSingular,
	childLabelPlural,
	emptyChildrenTitle,
	emptyChildrenDescription,
	createChildText,
	onEditEntity,
	onDeleteEntity,
	onEditChild,
	onRequestDeleteChild,
	onViewDetails,
	onCreateChild,
	alwaysShowDetailsButton = false,
	onViewMembers,
}: EntityGroupCardProps) => {
	const childCount = childItems.length;
	const showOverflowFooter = childCount > 4;
	const overflowCount = childCount - 4;
	const visibleChildren = childItems.slice(0, 4);
	const isEmptyChildren = childCount === 0;

	return (
		<article className="flex h-full min-h-[25rem] flex-col rounded-[1.25rem] border border-stone-100 bg-white p-5 shadow-sm transition-all hover:border-stone-200 hover:shadow-md">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 flex-1 gap-3">
					<div
						className={cn(
							'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xs font-black',
							iconClassName,
						)}
					>
						{initials}
					</div>
					<div className="min-w-0 flex-1">
						<h3 className="truncate text-base font-bold leading-tight text-stone-900">
							{entityName}
						</h3>
						<p className="mt-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
							{childCount === 1
								? `1 ${childLabelSingular}`
								: `${childCount} ${childLabelPlural}`}
						</p>
					</div>
				</div>
				<div className="flex shrink-0 gap-0.5">
					<button
						type="button"
						onClick={onEditEntity}
						className="rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
						aria-label="Editar item"
					>
						<Pencil size={18} strokeWidth={2} />
					</button>
					<button
						type="button"
						onClick={onDeleteEntity}
						className="rounded-full p-2 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
						aria-label="Excluir item"
					>
						<Trash2 size={18} strokeWidth={2} />
					</button>
					<button
						type="button"
						onClick={onCreateChild}
						className="ml-3 rounded-full bg-black p-2.5 text-white transition-colors hover:bg-neutral-800"
						aria-label={`Adicionar ${childLabelSingular.toLowerCase()}`}
					>
						<Plus size={18} strokeWidth={2.8} />
					</button>
				</div>
			</div>

			<div
				className={cn(
					'mt-4 flex min-h-0 flex-col gap-1.5 overflow-hidden',
					isEmptyChildren
						? 'min-h-[14rem] flex-1'
						: 'h-[14rem] shrink-0',
				)}
			>
				{visibleChildren.length > 0 ? (
					visibleChildren.map((child, childIndex) => (
						<div
							key={`${entityId}-child-${childIndex}`}
							className="flex shrink-0 items-center gap-1 rounded-xl border border-stone-200 bg-white px-2 py-2 pl-3 transition-colors hover:border-stone-300 hover:bg-stone-50/80"
						>
							<button
								type="button"
								className="min-w-0 flex-1 truncate text-left text-sm font-semibold text-stone-800"
								onClick={() => onEditChild(childIndex)}
							>
								{child}
							</button>
							<button
								type="button"
								onClick={() => onEditChild(childIndex)}
								className="shrink-0 rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-900"
								aria-label={`Editar ${child}`}
							>
								<Pencil size={16} strokeWidth={2} />
							</button>
							<button
								type="button"
								onClick={() => onRequestDeleteChild(childIndex)}
								className="shrink-0 rounded-full p-2 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
								aria-label={`Excluir ${child}`}
							>
								<Trash2 size={16} strokeWidth={2} />
							</button>
						</div>
					))
				) : (
					<div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 px-4 py-8 text-center">
						<div className="mb-3 rounded-full bg-white p-3 text-stone-300 shadow-sm">
							<Layers className="size-6" strokeWidth={1.75} />
						</div>
						<h4 className="text-sm font-bold text-stone-900">
							{emptyChildrenTitle}
						</h4>
						<p className="mt-1 max-w-[14rem] text-xs leading-relaxed text-stone-500">
							{emptyChildrenDescription}
						</p>
						<Button
							text={createChildText}
							variant="outline"
							className="mt-4 rounded-full border-stone-300 px-5 py-2 font-bold"
							startIcon={<Plus className="size-4 stroke-[3]" />}
							onClick={onCreateChild}
						/>
					</div>
				)}
			</div>

			{(alwaysShowDetailsButton ||
				showOverflowFooter ||
				!isEmptyChildren) && (
				<div
					className={cn(
						'mt-auto flex min-h-[3.25rem] flex-col justify-center pt-4',
						showOverflowFooter && 'border-t border-stone-100',
					)}
				>
					{showOverflowFooter ? (
						<div className="flex items-center justify-between gap-3">
							<div className="flex min-w-0 items-center gap-2">
								{onViewMembers ? (
									<button
										type="button"
										onClick={onViewMembers}
										className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 text-stone-500 transition-colors hover:border-stone-300 hover:bg-stone-50 hover:text-stone-900"
										title="Ver colaboradores"
										aria-label="Ver colaboradores deste departamento"
									>
										<Eye
											className="size-[18px]"
											strokeWidth={2}
										/>
									</button>
								) : null}
								<span className="flex h-8 min-w-[2.25rem] items-center justify-center rounded-full border border-stone-200 bg-stone-100 px-2 text-xs font-bold text-stone-600">
									+{overflowCount}
								</span>
							</div>
							<button
								type="button"
								onClick={onViewDetails}
								className="inline-flex shrink-0 items-center gap-0.5 text-sm font-bold text-stone-500 transition-colors hover:text-stone-800"
							>
								Ver detalhes
								<ChevronRight
									className="size-4"
									strokeWidth={2.5}
								/>
							</button>
						</div>
					) : alwaysShowDetailsButton ? (
						<div className="flex items-center justify-between gap-3">
							{onViewMembers ? (
								<button
									type="button"
									onClick={onViewMembers}
									className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 text-stone-500 transition-colors hover:border-stone-300 hover:bg-stone-50 hover:text-stone-900"
									title="Ver colaboradores"
									aria-label="Ver colaboradores deste departamento"
								>
									<Eye
										className="size-[18px]"
										strokeWidth={2}
									/>
								</button>
							) : (
								<span className="min-w-[2.25rem]" aria-hidden />
							)}
							<button
								type="button"
								onClick={onViewDetails}
								className="inline-flex shrink-0 items-center gap-0.5 text-sm font-bold text-stone-500 transition-colors hover:text-stone-800"
							>
								Ver detalhes
								<ChevronRight
									className="size-4"
									strokeWidth={2.5}
								/>
							</button>
						</div>
					) : (
						<div className="min-h-[2.5rem]" aria-hidden />
					)}
				</div>
			)}
		</article>
	);
};
